import { AccountProvider, Client } from '@collinsonx/constants/enums';
import {
  loggerAction,
  loggerError,
  loggerInfo,
  loggerWarn,
} from '@collinsonx/utils/lib/analytics';
import { datadogRum } from '@datadog/browser-rum';
import {
  getItem,
  getLocalStorageItem,
  JSONParse,
  log,
  removeItem,
  setItem,
  setLocalStorageItem,
} from '@lib';
import {
  ANALYTICS_TAGS,
  BookingError,
  BookingInfo,
  BookingWarn,
  DATADOG_ERROR_LOGS,
  DATADOG_INFO_LOGS,
  DATADOG_WARN_LOGS,
  PAGENAMES,
} from 'config/datadog';
import { useLogsAndEvents } from 'config/features';
import { ROUTES } from 'config/navigation';
import {
  IS_USER_VALIDATED,
  LOG_ACTION_PATH,
  LOG_DATA_ACCOUNT_TYPE,
  LOG_DATA_BOOKING,
  LOG_DATA_CLIENT,
  LOG_DATA_DEVICE_ID,
  LOG_DATA_PAGE_NAME,
  LOG_DATA_PAYLOAD,
  LOG_DATA_PRODUCT,
  LOG_DATA_SESSION_INIT_ID,
  LOG_DATA_USER_ACCOUNT,
  LOG_DATA_USER_FLOW,
  LOG_DATA_USER_SESSION,
  LOG_FUNNEL_EMITTED_ACTIONS,
  LOG_INITIATE_USER_FLOW_FIRED,
} from 'config/storage';
import { getProductType } from 'utils/datadog';
import { clientIsReady } from 'utils/errorHandling';

export const Unknown = 'Unknown';

export const serializeError = (error: unknown) => {
  if (error === null || error === undefined) return error;

  if (error instanceof Error) {
    const { message, name, stack, ...customProps } = error as Error &
      Record<string, unknown>;
    return { message, name, stack, ...customProps };
  }

  if (typeof error === 'object') return error;

  return { message: String(error) };
};

type LogStoragePayload = {
  accountProvider: AccountProvider;
  externalID: string;
  language: string;
  loungeCode: string;
  membershipNumber?: string;
  membershipType: Client;
  platform: string;
  referrer?: string;
  sourceCode?: string;
  version: string;
};

type ClientLogDataT = {
  issuer?: string;
  planName?: string;
};

export enum UserFlow {
  'Amend' = 'Amend',
  'Cancel' = 'Cancel',
  'Prebook' = 'Prebook',
}

type BookingLogDataT = {
  bookingID?: string;
  bookingReference?: string;
  bookingStatus?: string;
};

type UserAccountLogDataT = {
  consumerID?: string;
  linkedAccountID?: string;
  membershipID?: string;
};

type UserSessionLogDataT = {
  sessionHandle: string;
  userId: string;
};

export enum OriginType {
  'API' = 'API',
  'App' = 'App',
  'External' = 'External',
  'Platform' = 'Platform',
  'Unhandled' = 'Unhandled',
  'User' = 'User',
}

export enum AccountType {
  'EXTERNAL_ID_AND_SC' = 'EXTERNAL_ID_AND_SC',
  'MEMBERSHIP_NUM' = 'MEMBERSHIP_NUM',
  'MEMBERSHIP_NUM_AND_SC' = 'MEMBERSHIP_NUM_AND_SC',
  'UNHANDLED' = 'UNHANDLED',
}

type ErrorLogDataParam = { origin: OriginType } & Record<string, unknown>;

const setRumSessionContext = (context: Record<string, unknown>) => {
  Object.entries(context).forEach(([key, val]) => {
    if (val !== undefined) {
      datadogRum.setGlobalContextProperty(key, val);
    }
  });
};

export const useLogStorage = () => {
  const setPayloadData = (payload: Partial<LogStoragePayload>) => {
    setItem(LOG_DATA_PAYLOAD, JSON.stringify(payload));
  };

  const getPayloadData = (): LogStoragePayload => {
    const payload = JSONParse(getItem(LOG_DATA_PAYLOAD), {});

    return {
      accountProvider: payload?.accountProvider ?? Unknown,
      externalID: payload?.externalID ?? Unknown,
      language: payload?.language ?? Unknown,
      loungeCode: payload?.loungeCode ?? Unknown,
      membershipNumber: payload?.membershipNumber,
      membershipType: payload?.membershipType ?? Unknown,
      platform: payload?.platform ?? Unknown,
      referrer: payload?.referrer,
      sourceCode: payload?.sourceCode,
      version: payload?.version ?? Unknown,
    };
  };

  const setUserFlowData = (userFlow: UserFlow) => {
    setItem(LOG_DATA_USER_FLOW, userFlow);
  };

  const getUserFlowData = () =>
    (getItem(LOG_DATA_USER_FLOW) as UserFlow) || Unknown;

  const setAccountType = (accountType: AccountType) => {
    setItem(LOG_DATA_ACCOUNT_TYPE, accountType);
  };

  const getAccountType = () =>
    (getItem(LOG_DATA_ACCOUNT_TYPE) as AccountType) || AccountType.UNHANDLED;

  const setBookingData = (booking: BookingLogDataT) => {
    setItem(LOG_DATA_BOOKING, JSON.stringify(booking));
  };

  const getBookingData = (): BookingLogDataT => {
    const booking = JSONParse(getItem(LOG_DATA_BOOKING), {});

    return {
      bookingID: booking.bookingID,
      bookingReference: booking.bookingReference,
      bookingStatus: booking.bookingStatus,
    };
  };

  const setUserAccountData = (userAccount: UserAccountLogDataT) =>
    setItem(LOG_DATA_USER_ACCOUNT, JSON.stringify(userAccount));

  const getUserAccountData = (): UserAccountLogDataT => {
    const userAccount = JSONParse(getItem(LOG_DATA_USER_ACCOUNT), {});

    return {
      consumerID: userAccount?.consumerID ?? Unknown,
      linkedAccountID: userAccount?.linkedAccountID ?? Unknown,
      membershipID: userAccount?.membershipID ?? Unknown,
    };
  };

  const setProductData = (product: { productID: string | undefined }) => {
    setItem(LOG_DATA_PRODUCT, JSON.stringify(product));
  };

  const getProductData = (): { productID: string } => {
    const product = JSONParse(getItem(LOG_DATA_PRODUCT), {});
    return { productID: product.productID ?? Unknown };
  };

  const setRouteData = (pageName: ROUTES) => {
    setItem(LOG_DATA_PAGE_NAME, pageName);
  };

  const getRouteData = () => (getItem(LOG_DATA_PAGE_NAME) as ROUTES) || Unknown;

  const setClientData = (client: ClientLogDataT) => {
    setItem(LOG_DATA_CLIENT, JSON.stringify(client));
  };

  const getClientData = (): ClientLogDataT => {
    const client = JSONParse(getItem(LOG_DATA_CLIENT), {});

    return {
      issuer: client?.issuer ?? Unknown,
      planName: client?.planName ?? Unknown,
    };
  };

  const setUserSessionData = (userSession: UserSessionLogDataT) => {
    setItem(LOG_DATA_USER_SESSION, JSON.stringify(userSession));
  };

  const getUserSessionData = (): UserSessionLogDataT => {
    const userSession = JSONParse(getItem(LOG_DATA_USER_SESSION), {});

    return {
      sessionHandle: userSession.sessionHandle ?? Unknown,
      userId: userSession.userId ?? Unknown,
    };
  };

  const getDeviceIdData = () =>
    getLocalStorageItem(LOG_DATA_DEVICE_ID) || Unknown;

  const getSessionInitIdData = () =>
    getLocalStorageItem(LOG_DATA_SESSION_INIT_ID) || Unknown;

  const setDeviceAndSessionIDsData = () => {
    if (!clientIsReady) return;

    setLocalStorageItem(LOG_DATA_SESSION_INIT_ID, crypto.randomUUID());

    if (getDeviceIdData() !== Unknown) return;

    setLocalStorageItem(LOG_DATA_DEVICE_ID, crypto.randomUUID());
  };

  return {
    getAccountType,
    getBookingData,
    getClientData,
    getDeviceIdData,
    getPayloadData,
    getProductData,
    getRouteData,
    getSessionInitIdData,
    getUserAccountData,
    getUserFlowData,
    getUserSessionData,
    setAccountType,
    setBookingData,
    setClientData,
    setDeviceAndSessionIDsData,
    setPayloadData,
    setProductData,
    setRouteData,
    setUserAccountData,
    setUserFlowData,
    setUserSessionData,
  };
};

const getUnauthorizedUserContext = () => {
  const {
    getClientData,
    getDeviceIdData,
    getPayloadData,
    getSessionInitIdData,
  } = useLogStorage();

  const payloadData = getPayloadData();

  return {
    ...payloadData,
    ...getClientData(),
    deviceID: getDeviceIdData(),
    productType: getProductType(payloadData.loungeCode),
    sessionInitID: getSessionInitIdData(),
  };
};

const getAuthorizedUserContext = () => {
  const { getUserAccountData, getUserSessionData } = useLogStorage();

  return {
    ...getUserAccountData(),
    ...getUserSessionData(),
  };
};

const getDynamicLogContext = () => {
  const {
    getAccountType,
    getBookingData,
    getProductData,
    getRouteData,
    getUserFlowData,
  } = useLogStorage();

  return {
    ...getBookingData(),
    ...getProductData(),
    accountType: getAccountType(),
    route: getRouteData(),
    userFlow: getUserFlowData(),
  };
};

const getBaseLogContext = () => ({
  ...getUnauthorizedUserContext(),
  ...getAuthorizedUserContext(),
  ...getDynamicLogContext(),
});

export const hydrateUnauthorizedUserContext = () => {
  const context = getUnauthorizedUserContext();
  setRumSessionContext(context);
};

export const hydrateAuthorizedUserContext = () => {
  const context = getAuthorizedUserContext();
  setRumSessionContext(context);
};

const hydrateSessionDynamicData = () => {
  const context = getDynamicLogContext();
  setRumSessionContext(context);
  return context;
};

const logToLocalConsole = (
  key: string,
  description: string,
  data?: Record<string, unknown>
) => log(`${key}: ${description}`, data);

export const sendInitiateUserFlowLog = (userFlow: UserFlow) => {
  if (getItem(LOG_INITIATE_USER_FLOW_FIRED) === userFlow) return;
  setItem(LOG_INITIATE_USER_FLOW_FIRED, userFlow);
  sendInfoLog(BookingInfo.INITIATE_USER_FLOW, { userFlow });
};

export const sendCompleteUserFlowLog = (userFlow: UserFlow) => {
  if (getItem(LOG_INITIATE_USER_FLOW_FIRED) !== userFlow) return;
  removeItem(LOG_INITIATE_USER_FLOW_FIRED);
  sendInfoLog(BookingInfo.COMPLETE_USER_FLOW, { userFlow });
};

export const sendInfoLog = (
  key: BookingInfo,
  data?: Record<string, unknown>
) => {
  if (!useLogsAndEvents) return;

  const context = getBaseLogContext();

  logToLocalConsole(key, DATADOG_INFO_LOGS[key], {
    ...context,
    ...(data || {}),
  });

  loggerInfo({
    data: { ...context, ...(data || {}) },
    description: DATADOG_INFO_LOGS[key],
    key,
  });
};

// corner case
export const sendWarnLog = (key: BookingWarn, data: ErrorLogDataParam) => {
  if (!useLogsAndEvents) return;

  const context = getBaseLogContext();

  logToLocalConsole(key, DATADOG_WARN_LOGS[key], {
    ...context,
    ...data,
  });

  loggerWarn({
    data: { ...context, ...(data || {}) },
    description: DATADOG_WARN_LOGS[key],
    key,
  });
};

// flow blocking
export const sendErrorLog = (key: BookingError, data: ErrorLogDataParam) => {
  if (!useLogsAndEvents) return;

  const context = getBaseLogContext();

  logToLocalConsole(key, DATADOG_ERROR_LOGS[key], {
    ...context,
    ...data,
  });

  loggerError({
    data: { ...context, ...(data || {}) },
    description: DATADOG_ERROR_LOGS[key],
    key,
  });
};

const FUNNEL_CREATE_BOOKING = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_PAGE_ENTER_CHECKAVAILABILITY,
  ANALYTICS_TAGS.ON_CONTINUE_BUTTON_AVI,
  ANALYTICS_TAGS.ON_SLOT_PG_ENTER,
  ANALYTICS_TAGS.ON_SLOT_LIST_RENDERED,
  ANALYTICS_TAGS.ON_SLOT_CHANGE,
  ANALYTICS_TAGS.ON_SLOT_CONTINUE,
  ANALYTICS_TAGS.ON_CONFIRM_BOOKING_PG_ENTER,
  ANALYTICS_TAGS.ON_PAYMENT_CONTINUE,
  ANALYTICS_TAGS.ON_PAGE_ENTER_CONFIRMED,
  ANALYTICS_TAGS.ON_BOOKING_FINALIZED,
]);

const FUNNEL_AMENDMENT = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_AMEND_BOOKING_PG_ENTER,
  ANALYTICS_TAGS.ON_CONTINUE_BUTTON_AVI_EDIT,
  ANALYTICS_TAGS.ON_SLOT_AMEND_ENTER,
  ANALYTICS_TAGS.ON_SLOT_AMEND_CONFIRMED,
  ANALYTICS_TAGS.ON_CONFIRM_AMEND_SAVE,
  ANALYTICS_TAGS.ON_PAGE_ENTER_CONFIRMED_AMEND,
  ANALYTICS_TAGS.ON_CONFIRM_AMEND_PG,
]);

const FUNNEL_CANCELLATION = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_CANCEL_BOOKING_PG_ENTER,
  ANALYTICS_TAGS.ON_CANCEL_MODAL_OPEN,
  ANALYTICS_TAGS.ON_CANCEL_CONFIRM_CLICK,
  ANALYTICS_TAGS.ON_CANCELLED_BOOKING_PG_ENTER,
]);

const FUNNEL_LOGIN = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_PAGE_ENTER_EMAIL,
  ANALYTICS_TAGS.ON_CONTINUE_CLICK,
  ANALYTICS_TAGS.ON_CHECK_CODE_ENTER,
  ANALYTICS_TAGS.ON_CHECK_CODE_VERIFY,
  ANALYTICS_TAGS.ON_LOGIN_SUCCESS,
]);

const FUNNEL_REGISTER = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_SIGNUP_PAGE_ENTER,
  ANALYTICS_TAGS.ON_SIGNUP_PAGE_CONFIRM,
  ANALYTICS_TAGS.ON_SIGNUP_SUCCESS,
]);

const FUNNEL_STEPS = new Set<ANALYTICS_TAGS>([
  ...FUNNEL_CREATE_BOOKING,
  ...FUNNEL_AMENDMENT,
  ...FUNNEL_CANCELLATION,
  ...FUNNEL_LOGIN,
  ...FUNNEL_REGISTER,
]);

const PROTECTED_FUNNEL_STEPS = new Set<ANALYTICS_TAGS>([
  ...FUNNEL_CREATE_BOOKING,
  ...FUNNEL_AMENDMENT,
  ...FUNNEL_CANCELLATION,
]);

const FLOW_START_ACTIONS = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_PAGE_ENTER_CHECKAVAILABILITY,
  ANALYTICS_TAGS.ON_AMEND_BOOKING_PG_ENTER,
  ANALYTICS_TAGS.ON_CANCEL_BOOKING_PG_ENTER,
  ANALYTICS_TAGS.ON_PAGE_ENTER_EMAIL,
  ANALYTICS_TAGS.ON_SIGNUP_PAGE_ENTER,
]);

const FLOW_END_ACTIONS = new Set<ANALYTICS_TAGS>([
  ANALYTICS_TAGS.ON_BOOKING_FINALIZED,
  ANALYTICS_TAGS.ON_CONFIRM_AMEND_PG,
  ANALYTICS_TAGS.ON_CANCELLED_BOOKING_PG_ENTER,
  ANALYTICS_TAGS.ON_LOGIN_SUCCESS,
  ANALYTICS_TAGS.ON_SIGNUP_SUCCESS,
]);

const getEmittedFunnelActions = (): ANALYTICS_TAGS[] => {
  const raw = JSONParse(getItem(LOG_FUNNEL_EMITTED_ACTIONS), []);
  return Array.isArray(raw) ? raw : [];
};

const persistEmittedFunnelActions = (emitted: string[]) => {
  setItem(LOG_FUNNEL_EMITTED_ACTIONS, JSON.stringify(emitted));
};

const ACTION_PATH_MAX_LENGTH = 100;

const appendToActionPath = (action: ANALYTICS_TAGS): ANALYTICS_TAGS[] => {
  const raw = JSONParse(getItem(LOG_ACTION_PATH), []);
  const path: ANALYTICS_TAGS[] = Array.isArray(raw) ? raw : [];
  path.push(action);
  const trimmed =
    path.length > ACTION_PATH_MAX_LENGTH
      ? path.slice(path.length - ACTION_PATH_MAX_LENGTH)
      : path;
  setItem(LOG_ACTION_PATH, JSON.stringify(trimmed));
  return trimmed;
};

type FunnelActionResult = {
  block: boolean;
  status?: 'ACTIVE' | 'COMPLETED';
};

const handleFunnelAction = (action: ANALYTICS_TAGS): FunnelActionResult => {
  if (!FUNNEL_STEPS.has(action)) return { block: false };

  const isStartAction = FLOW_START_ACTIONS.has(action);

  const funnelActions = getEmittedFunnelActions();
  const funnelStart = funnelActions[0];
  const funnelLast = funnelActions[funnelActions.length - 1];

  const isFunnelCompleted = FLOW_END_ACTIONS.has(funnelLast);
  const isNewFlow = isStartAction && funnelStart !== action;
  const isRestartingFlow = isStartAction && isFunnelCompleted;

  const emitted = isNewFlow || isRestartingFlow ? [] : funnelActions;

  if (emitted.includes(action)) return { block: true };
  emitted.push(action);
  persistEmittedFunnelActions(emitted);

  const funnelHasEndAction = emitted.some((a) =>
    FLOW_END_ACTIONS.has(a as ANALYTICS_TAGS)
  );

  return {
    block: false,
    status: funnelHasEndAction ? 'COMPLETED' : 'ACTIVE',
  };
};

export const sendActionLog = (
  action: ANALYTICS_TAGS,
  page: PAGENAMES,
  data?: Record<string, any>
) => {
  if (!useLogsAndEvents) return;

  if (
    PROTECTED_FUNNEL_STEPS.has(action) &&
    getItem(IS_USER_VALIDATED) !== 'true'
  )
    return;

  const { block, status } = handleFunnelAction(action);
  if (block) return;

  const path = appendToActionPath(action);

  const context = hydrateSessionDynamicData();

  const dataToSend = {
    ...context,
    ...(data || {}),
    ...(status && { status }),
    path,
  };

  logToLocalConsole('Action', action, dataToSend);

  loggerAction({
    action,
    data: dataToSend,
    page,
  });
};

let loginRedirectCount = 0;
const redirectReason: string[] = [];

export const resetLoginRedirectCount = () => {
  loginRedirectCount = 0;
  redirectReason.length = 0;
};

export const LOGOUT_REASON = {
  AUTHENTICATED_USER: {
    PAYLOAD_MISMATCH:
      'Payload mismatch between cached JWT and query JWT during initialization',
    INVALID_SESSION: 'User session is invalid during initialization',
    INVALID_CONSUMER: 'Consumer validation failed during user validation',
    MISSING_LINKED_ACCOUNT_AND_MEMBERSHIP:
      'Missing linked account and membership during user validation',
  },
  UNAUTHENTICATED_USER: {
    MEMBERSHIP_ALREADY_CONNECTED:
      'Membership already connected during link account',
  },
};

const authenticatedLogoutReasons = Object.values(
  LOGOUT_REASON.AUTHENTICATED_USER
);
const getAuthenticatedLogoutsCount = () =>
  redirectReason.filter((reason) => authenticatedLogoutReasons.includes(reason))
    .length;

export const trackLoginRedirect = (data?: Record<string, unknown>) => {
  loginRedirectCount += 1;
  sendInfoLog(BookingInfo.REDIRECT_TO_LOGIN, data);
  redirectReason.push((data?.reason as string) ?? Unknown);

  if (loginRedirectCount <= 1) return;

  if (getAuthenticatedLogoutsCount() > 1) {
    sendErrorLog(BookingError.MULTIPLE_LOGOUTS_INITIATED, {
      origin: OriginType.App,
      reasonChain: redirectReason.join(' -> '),
    });
  } else {
    sendWarnLog(BookingWarn.MULTIPLE_LOGIN_ATTEMPTS, {
      origin: OriginType.App,
      reasonChain: redirectReason.join(' -> '),
    });
  }
};
